import { useEffect, useMemo, useRef } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import { useUnmount } from "app/custom-hooks/useUnmount";
import { markInitialFiltersReady } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { applyTerritoryTableSort } from "app/pages/sales-intelligence/modules/leads-search";
import { setLeadsFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/action-creators";
import { selectFiltersValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/selectors";
import type { RootState } from "single-spa/store/types";

import { selectTeamsWithFilters, useTeamsListQuery } from "../queries/useTeamsListQuery";
import { resetTerritory, setSelectedTeamId, setTerritoryEnabled } from "../store/action-creators";
import { selectSelectedTeamId, selectTerritoryEnabled } from "../store/selectors";
import type { TeamFilters, TeamListItem } from "../types/teams.types";
import { applyTeamFilterOverlay, type TerritoryOverlayFilterInstance } from "../utils/applyTeamFilterOverlay";

const EMPTY_TEAMS: readonly TeamListItem[] = [];

/**
 * Drives "My territory filters": fetches the user's teams, defaults to the first one, and keeps the
 * leads filters in sync with the toggle/selected-team via {@link applyTeamFilterOverlay}.
 *
 * Mount **once**. The caller owns the route/feature eligibility and passes it via {@link isEligible};
 * the hook self-gates on that flag and stays agnostic of the page it runs on.
 *
 * `hasInitialSeed`: a seed is an explicit deep-link intent, so it turns the territory toggle off once (any
 * previously-applied team is stripped by that pass). From there the toggle is the user's: turning it back
 * on overlays the team onto the seeded values, and the team wins on any shared key.
 */
export const useApplyTerritoryFilters = (
    isEligible: boolean,
    filterInstances: readonly TerritoryOverlayFilterInstance[] = [],
    hasInitialSeed = false,
): void => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const filterInstancesRef = useRef(filterInstances);
    filterInstancesRef.current = filterInstances;

    // Only teams that actually have filters constitute a territory — derived in the query's `select`.
    const {
        data: teamsWithFilters = EMPTY_TEAMS,
        isError,
        isSuccess,
    } = useTeamsListQuery({ onlyMyTeams: true, enabled: isEligible, select: selectTeamsWithFilters });

    const isTeamsListLoaded = isSuccess || isError;

    const isEnabled = useSelector(selectTerritoryEnabled);
    const selectedTeamId = useSelector(selectSelectedTeamId);

    const selectedTeamFilters = useMemo(
        () => teamsWithFilters.find((team) => team.teamId === selectedTeamId)?.filters,
        [teamsWithFilters, selectedTeamId],
    );

    const appliedTeamFiltersRef = useRef<TeamFilters | undefined>(undefined);
    const isSeedSettledRef = useRef(false);

    useEffect(() => {
        if (!isEligible || teamsWithFilters.length === 0) {
            return;
        }
        const isSelectionValid = teamsWithFilters.some((team) => team.teamId === selectedTeamId);
        if (!isSelectionValid) {
            dispatch(setSelectedTeamId(teamsWithFilters[0].teamId));
        }
    }, [isEligible, teamsWithFilters, selectedTeamId, dispatch]);

    // Re-apply the overlay whenever the toggle or selected team changes. Reads current filters and
    // filterInstances on demand (not via deps) so the user's own edits are never reverted.
    useEffect(() => {
        if (!isEligible || !isTeamsListLoaded || teamsWithFilters.length === 0) {
            return;
        }

        // A seed turns territory off once; the pass that follows strips any applied team. Guarded on
        // `isEnabled` because dispatching `false` onto `false` re-renders nothing, stranding readiness.
        if (hasInitialSeed && !isSeedSettledRef.current) {
            isSeedSettledRef.current = true;

            if (isEnabled) {
                dispatch(setTerritoryEnabled(false));
                return;
            }
        }

        const nextTeam = isEnabled ? selectedTeamFilters : undefined;

        // TODO ADR-0008: this territory composition (overlay onto current) relocates to advanced-search-composition.
        const current = selectFiltersValue(store.getState());
        const prevTeam = appliedTeamFiltersRef.current;
        dispatch(setLeadsFilterValue(applyTeamFilterOverlay(current, prevTeam, nextTeam, filterInstancesRef.current)));
        dispatch(applyTerritoryTableSort(isEnabled && selectedTeamFilters !== undefined));
        appliedTeamFiltersRef.current = nextTeam;

        const isSelectionSettled = !isEnabled || selectedTeamFilters !== undefined;
        if (isSelectionSettled) {
            dispatch(markInitialFiltersReady());
        }
        // intentionally not keyed on the live filters value or filterInstances identity
    }, [isEligible, isTeamsListLoaded, isEnabled, selectedTeamFilters, dispatch, store, hasInitialSeed]);

    useEffect(() => {
        if (!isEligible || !isTeamsListLoaded || teamsWithFilters.length > 0) {
            return;
        }
        dispatch(markInitialFiltersReady());
    }, [isEligible, isTeamsListLoaded, teamsWithFilters.length, dispatch]);

    useUnmount(() => {
        dispatch(resetTerritory());
        appliedTeamFiltersRef.current = undefined;
    });
};
